import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Combobox } from "@/components/combobox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCurrentUser } from "@/hooks/use-current-user";
import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

interface AgentOption {
  id: string;
  fullName: string;
  email: string | null;
}

interface SetTravelAgencyAgentProps {
  claimId: string;
  currentAdmin?: { id?: string; fullName?: string; email?: string | null } | null;
}

/** Port of overview/SetTravelAgencyAgent.vue — assigns the travel-agency agent of the claim. */
export function SetTravelAgencyAgent({ claimId, currentAdmin }: SetTravelAgencyAgentProps) {
  const queryClient = useQueryClient();
  const { can } = useCurrentUser();
  const canEdit = can([...UPCLAIM_ROLES, "CUSTOMER_ADMIN", "CUSTOMER_AGENT"]);

  const { data: agents = [] } = useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "travel-agency-agents"],
    queryFn: async () => {
      const { admins } = await adminAPI.listTravelAgencyAgentsForClaim(claimId);
      return (admins ?? []) as AgentOption[];
    },
    enabled: canEdit,
  });

  const setAgent = useMutation({
    mutationFn: (agent: AgentOption | null) =>
      notify.promise(
        adminAPI.setTravelAgencyAgentForClaim(claimId, agent ? { adminId: agent.id } : { unset: true }),
        { success: "Agent enregistré" },
      ),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });

  if (!canEdit) {
    return <span>{currentAdmin?.fullName ?? "Aucun"}</span>;
  }

  const selected = agents.find((agent) => agent.id === currentAdmin?.id) ?? null;

  const combobox = (
    <Combobox
      items={agents}
      value={selected}
      onChange={(agent) => setAgent.mutate(agent)}
      getKey={(agent) => agent.id}
      getLabel={(agent) => agent.fullName}
      getSearchValue={(agent) => agent.email ?? ""}
      placeholder={currentAdmin?.fullName ?? "Aucun"}
      searchPlaceholder="Rechercher..."
      emptyMessage="Aucun agent trouvé"
      unselectLabel="Aucun"
    />
  );

  if (!currentAdmin?.email) {
    return combobox;
  }

  return (
    <Tooltip>
      <TooltipTrigger render={<span className="block" />}>{combobox}</TooltipTrigger>
      <TooltipContent>{currentAdmin.email}</TooltipContent>
    </Tooltip>
  );
}
